import { createFileRoute } from "@tanstack/react-router";
import { ogImageMeta } from "@/lib/seo/og-image";
import { LocaleLink } from "@/components/site/LocaleLink";
import { useState, useRef, useEffect } from "react";
import { Mail, MapPin, CheckCircle2, ArrowRight } from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Button } from "@/components/ui/button";
import { contactSchema } from "@/lib/contact/schema";
import { toast } from "sonner";
import { track, trackFormFailure, trackFormSubmit } from "@/lib/analytics/track";
import { failureReasonForStatus } from "@/lib/analytics/cta-events";

import { pageSchemas } from "@/lib/seo/page-schema";
import { logFormSubmission, hubspotFailReason } from "@/lib/form-log/client";
export const Route = createFileRoute("/contact-us")({
  head: () => ({
    meta: [
      { title: "Contact Perceptica - Media Monitoring Services in SEE" },
      { name: "description", content: "Talk to the Perceptica team about media monitoring services, media intelligence and media analysis tailored for Southeast Europe. Most requests receive a reply from us within one business day." },
      { property: "og:title", content: "Contact Perceptica - Media Monitoring Services in SEE" },
      { property: "og:description", content: "Talk to the Perceptica team about media monitoring services, media intelligence and media analysis tailored for Southeast Europe. Most requests receive a reply from us within one business day." },

      ...ogImageMeta("en", "/contact-us", "Contact the Perceptica team"),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Contact Perceptica - Media Monitoring Services in SEE" },
      { name: "twitter:description", content: "Talk to the Perceptica team about media monitoring services, media intelligence and media analysis tailored for Southeast Europe. Most requests receive a reply from us within one business day." },
      { property: "og:url", content: "https://perceptica.com/contact-us" },
      { property: "og:type", content: "website" },
    ],
  
    scripts: pageSchemas({ locale: "en", path: "/contact-us", contact: true }),
  }),
  component: ContactPage,
});

function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submission, setSubmission] = useState<{
    name: string;
    email: string;
    company: string;
    message: string;
    ticketId: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const successRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (submitted && successRef.current) {
      successRef.current.focus();
      successRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [submitted]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setServerError(null);
    setErrors({});
    const fd = new FormData(e.currentTarget);
    const payload = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      company: String(fd.get("company") ?? ""),
      message: String(fd.get("message") ?? ""),
      consent: fd.get("consent") === "on",
      website: String(fd.get("website") ?? ""),
      pagePath: typeof window !== "undefined" ? window.location.pathname : "",
      formName: "contact-us",
    };
    const parsed = contactSchema.safeParse(payload);
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors;
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(flat)) if (v?.[0]) next[k] = v[0];
      setErrors(next);
      trackFormFailure({ form_name: payload.formName, reason: "validation_error", error_code: Object.keys(next).join(","), page_path: payload.pagePath, locale: "en" });
      return;
    }
    setSubmitting(true);
    const backupBase = {
      formType: "contact",
      formName: payload.formName,
      pagePath: payload.pagePath,
      name: parsed.data.name,
      email: parsed.data.email,
      company: parsed.data.company ?? undefined,
      message: parsed.data.message,
      payload: { locale: "en" },
    };
    try {
      const res = await fetch(
        "https://api-eu1.hsforms.com/submissions/v3/integration/submit/25788344/4c6068a3-cbe2-4c51-b63c-a28eb1ffa30a",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fields: [
              { objectTypeId: "0-1", name: "email", value: parsed.data.email },
              { objectTypeId: "0-1", name: "fullname", value: parsed.data.name },
              { objectTypeId: "0-1", name: "message", value: parsed.data.message },
              ...(parsed.data.company
                ? [{ objectTypeId: "0-1", name: "company", value: parsed.data.company }]
                : []),
            ],
            context: {
              pageUri: typeof window !== "undefined" ? window.location.href : "",
              pageName: typeof document !== "undefined" ? document.title : "",
            },
            legalConsentOptions: {
              consent: {
                consentToProcess: true,
                text: "I agree to the Perceptica team contacting me.",
              },
            },
          }),
        },
      );
      const data = (await res.json().catch(() => ({}))) as { inlineMessage?: string; message?: string };
      if (!res.ok) {
        void logFormSubmission({ ...backupBase, hubspotOk: false, hubspotReason: hubspotFailReason(res.status) });
        setServerError(data.message ?? "Something went wrong. Please try again.");
        toast.error(data.message ?? "Something went wrong. Please try again.");
        trackFormFailure({ form_name: payload.formName, reason: failureReasonForStatus(res.status), error_code: res.status, page_path: payload.pagePath, locale: "en" });
        return;
      }
      setSubmitted(true);
      toast.success("Message sent. Most requests receive a reply from us within one business day.");
      void logFormSubmission({ ...backupBase, hubspotOk: true });
      trackFormSubmit("submit_contact_us", { form_name: payload.formName, page_path: payload.pagePath, locale: "en" });
      setSubmission({
        name: parsed.data.name,
        email: parsed.data.email,
        company: parsed.data.company ?? "",
        message: parsed.data.message,
        ticketId: `PCP-${Date.now().toString(36).toUpperCase().slice(-6)}`,
      });
    } catch {
      void logFormSubmission({ ...backupBase, hubspotOk: false, hubspotReason: hubspotFailReason(null) });
      setServerError("Network error. Please try again.");
      toast.error("Network error. Please try again.");
      trackFormFailure({ form_name: payload.formName, reason: "network_error", page_path: payload.pagePath, locale: "en" });
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls =
    "w-full rounded-md border border-border/70 bg-background px-3 py-2.5 text-[14px] text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30";

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main" className="py-20 lg:py-28">
        <div className="mx-auto grid max-w-[1100px] gap-16 px-6 lg:grid-cols-[1fr_1.1fr] lg:px-10">
          <div>
            <span className="inline-flex items-center rounded-full border border-border/70 bg-secondary/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Contact
            </span>
            <h1 className="font-display mt-5 text-[clamp(2rem,3.6vw,3rem)] font-medium leading-[1.05] text-foreground">
              Talk to the Perceptica team.
            </h1>
            <p className="mt-5 max-w-md text-[15px] leading-relaxed text-foreground/75">
              A real person - no chatbot, no funnel. Most requests receive a reply from us within one business day.
            </p>

            <dl className="mt-9 space-y-4 text-[14px] text-foreground/85">
              <div className="flex items-start gap-3">
                <Mail className="mt-0.5 h-5 w-5 flex-none text-accent" />
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Email</dt>
                  <dd><a href="mailto:sales@perceptica.com" className="hover:text-accent">sales@perceptica.com</a></dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 flex-none text-accent" />
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Headquarters</dt>
                  <dd>117 Hristo Botev Blvd., 1303 Sofia</dd>
                </div>
              </div>
            </dl>

            <LocaleLink
              to="/request-a-demo"
              className="group mt-9 flex items-center justify-between gap-4 rounded-xl border border-border/60 bg-secondary/40 p-5 transition-colors hover:border-accent/50"
            >
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">
                  Looking for a walkthrough?
                </p>
                <p className="mt-1 text-[14px] font-medium text-foreground">
                  Request a product demo instead
                </p>
              </div>
              <ArrowRight className="h-4 w-4 flex-none text-accent transition-transform group-hover:translate-x-1" />
            </LocaleLink>
          </div>

          <div>
            {submitted && submission ? (
              <div
                ref={successRef}
                tabIndex={-1}
                role="status"
                aria-live="polite"
                className="overflow-hidden rounded-2xl border border-border/60 bg-background p-7 shadow-[var(--shadow-soft)] focus:outline-none"
              >
                <div className="flex items-start gap-4">
                  <span className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-accent/15 text-accent ring-1 ring-accent/30">
                    <CheckCircle2 className="h-6 w-6" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[12px] font-medium text-accent">Message sent</p>
                    <h2 className="font-display mt-1 text-[1.55rem] leading-tight text-foreground">
                      Thanks, {submission.name.split(" ")[0]} - we've got it.
                    </h2>
                    <p className="mt-2 text-[14px] leading-relaxed text-foreground/75">
                      Most requests receive a reply from us within one business day. Our reply will go to <span className="font-medium text-foreground">{submission.email}</span>.
                    </p>
                    <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                      Reference {submission.ticketId}
                    </p>
                  </div>
                </div>
                <div className="mt-7 flex flex-wrap gap-3 border-t border-border/60 pt-6">
                  <Button asChild size="lg">
                    <LocaleLink to="/request-a-demo">Book a demo</LocaleLink>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={() => {
                      setSubmitted(false);
                      setSubmission(null);
                    }}
                  >
                    Send another message
                  </Button>
                </div>
              </div>
            ) : (
              <form name="contact-us" id="contact-us" data-hs-do-not-collect="true" onSubmit={handleSubmit} noValidate className="space-y-5 rounded-2xl border border-border/60 bg-background p-7 shadow-[var(--shadow-soft)]">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Name" name="name" error={errors.name} inputCls={inputCls} required />
                  <Field label="Email" name="email" type="email" error={errors.email} inputCls={inputCls} required />
                </div>
                <Field label="Company (optional)" name="company" error={errors.company} inputCls={inputCls} />
                <div>
                  <label htmlFor="message" className="mb-1.5 block text-[12.5px] font-semibold text-foreground/90">
                    Message <span className="text-accent">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    className={inputCls}
                    placeholder="What would you like to talk about?"
                  />
                  {errors.message && <p className="mt-1 text-[12px] text-destructive">{errors.message}</p>}
                </div>

                {/* Honeypot */}
                <div className="hidden" aria-hidden>
                  <label>
                    Website
                    <input type="text" name="website" tabIndex={-1} autoComplete="off" />
                  </label>
                </div>

                <label className="flex items-start gap-3 text-[12.5px] text-foreground/75">
                  <input type="checkbox" name="consent" required className="mt-0.5 h-4 w-4 rounded border-border accent-accent" />
                  <span>
                    I agree to Perceptica processing my data per the{" "}
                    <LocaleLink to="/privacy-policy" className="font-semibold text-accent hover:underline">Privacy Policy</LocaleLink>.
                  </span>
                </label>
                {errors.consent && <p className="text-[12px] text-destructive">{errors.consent}</p>}

                {serverError && <p className="text-[13px] text-destructive">{serverError}</p>}

                <Button type="submit" size="lg" disabled={submitting} className="w-full sm:w-auto">
                  {submitting ? "Sending…" : "Send message"}
                </Button>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Most requests receive a reply from us within one business day.
                </p>
              </form>
            )}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  error,
  inputCls,
  required,
}: {
  label: string;
  name: string;
  type?: string;
  error?: string;
  inputCls: string;
  required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={name} className="mb-1.5 block text-[12.5px] font-semibold text-foreground/90">
        {label} {required && <span className="text-accent">*</span>}
      </label>
      <input id={name} name={name} type={type} required={required} className={inputCls} />
      {error && <p className="mt-1 text-[12px] text-destructive">{error}</p>}
    </div>
  );
}
